// src/pages/misc/onboarding/structureDraft.ts
// Shared company-structure draft. Public builder at /onboarding and the
// logged-in “create an organization” flow both read this same local draft.
//
// [HANDOFF] STORAGE KEYS used by the whole onboarding (all browser-only, nothing is sent to a server):
//   localStorage  ventaje_draft_v3        the StructureDraft below (survives refresh)
//   localStorage  ventaje_draft_v3_ready  '1' once the person reached the summary screen (S8) and continued
//   localStorage  ventaje_org_setup_v1    plan + extensions choice (orgSetup.ts)
//   sessionStorage ventaje_ob_person      first / last / email from the together sign-up (OnboardingAccountPage.tsx). NO password.
//   sessionStorage ventaje_s1_done        '1' when the landing quiz already answered "which are you" (skips builder screen S1)
//   sessionStorage ventaje_ob_structure_len  number of builder screens, so the progress bar stays consistent (continuousPath.ts)
// This draft is the "parsed" structure to send to the back end after sign-up/payment.
//
// [HANDOFF] START HERE. The new front end produces TWO ready-to-send outputs; nothing is sent to a server yet:
//   1. Onboarding -> checkout: structure (this file) + plan, cycle (monthly / 6 months / annual) and extensions with quantities
//      (orgSetup.ts) + person (OnboardingAccountPage.tsx) + the price (orderQuote.ts, `dueNow` = what Stripe charges today).
//      The full list and the suggested back-end order are at the top of CreateOrganizationBillingPage.tsx.
//   2. Partner program (/socios): submitPartner() and submitLead() in public/SociosPage.tsx build the partner and the referred lead.
//      Both are stubs; message text and the WhatsApp group link are in public/wa.ts.
import { type PriceBook, type StructureCounts, cheapestPlanThatFits, planLimits, planRow } from './priceBook';

export const DRAFT_KEY = 'ventaje_draft_v3';
export const DRAFT_READY_KEY = 'ventaje_draft_v3_ready';

// [HANDOFF] path: 0 = nothing picked yet, 1 = "Formo parte de una empresa" (employee; goes to sign-up/find your company, no structure),
// 2 = "Tengo mi empresa" (one company), 3 = "Administro varias empresas" (several companies).
export type PathId = 0 | 1 | 2 | 3;
// [HANDOFF] Child index -> parent index. Example { 4: 1 } means project #5 belongs to company #2. Missing key = not connected.
export type LinkMap = Record<number, number>;

// [HANDOFF] Counts come from the builder steppers, whose ceilings are structureCeilings() below: Elite's maximum from the price
// book where it states one, his ceiling (BUILDER_CEILINGS) where it states none (S-3). `empresas` only matters when path === 3.
// links = project -> company (path 3 only). assign = advisor -> team. Both are optional, skipped by "Omitir".
export interface StructureDraft {
  path: PathId;
  name: string;
  empresas: number;
  proyectos: number;
  equipos: number;
  asesores: number;
  admins: number;
  links: LinkMap;
  assign: LinkMap;
  /** When the autosave last wrote the draft (ISO); a draft older than DRAFT_TTL_DAYS reads as fresh (S-16). */
  savedAt?: string;
  /** The application P2 answered for this draft, once submitted (C-3); the resume and return routes read it. */
  applicationId?: string;
}

// [HANDOFF] Starting values shown when the person first reaches each stepper. They are sample numbers, not real data.
export function freshDraft(): StructureDraft {
  return {
    path: 0,
    name: '',
    empresas: 3,
    proyectos: 8,
    equipos: 2,
    asesores: 10,
    admins: 2,
    links: {},
    assign: {},
  };
}

/** The draft's life in the browser (S-16): past it the read answers a fresh draft; the paid account keeps nothing here. */
export const DRAFT_TTL_DAYS = 30;
const DRAFT_TTL_MS = DRAFT_TTL_DAYS * 24 * 60 * 60 * 1000;

/** His five counts, the stepper ceilings' shape. */
export type StructureCeilings = Pick<StructureDraft, 'empresas' | 'proyectos' | 'equipos' | 'asesores' | 'admins'>;

/** His stepper ceilings: the ceiling wherever Elite's card states no maximum (projects and admins today, S-3). */
export const BUILDER_CEILINGS: Readonly<StructureCeilings> = { empresas: 50, proyectos: 200, equipos: 20, asesores: 500, admins: 50 };

/** THE CEILINGS (S-3): Elite's maximum from the price book where it states one, his where it states none. null = THE FAILED READ. */
export function structureCeilings(book: PriceBook): StructureCeilings | null {
  const limits = planLimits(book, 'pro');
  if (limits === null) return null;
  return {
    empresas: limits.maxOrganizations ?? BUILDER_CEILINGS.empresas,
    proyectos: limits.maxProjects ?? BUILDER_CEILINGS.proyectos,
    equipos: limits.maxTeams ?? BUILDER_CEILINGS.equipos,
    asesores: limits.maxSalesUsers ?? BUILDER_CEILINGS.asesores,
    admins: limits.maxAdmins ?? BUILDER_CEILINGS.admins,
  };
}

const clampCount = (n: number, max: number) => Math.min(Math.max(0, n || 0), Math.max(0, max));

/** The draft brought inside the ceilings (S-16: an old draft over a limit is never sent as it is). */
export function clampStructureDraft(draft: StructureDraft, ceilings: StructureCeilings): StructureDraft {
  return {
    ...draft,
    empresas: clampCount(draft.empresas, ceilings.empresas),
    proyectos: clampCount(draft.proyectos, ceilings.proyectos),
    equipos: clampCount(draft.equipos, ceilings.equipos),
    asesores: clampCount(draft.asesores, ceilings.asesores),
    admins: clampCount(draft.admins, ceilings.admins),
  };
}

/** His counts in the server's words, for the book's limits (fits, cheapestPlanThatFits): one organization unless several are run. */
export function structureCounts(draft: StructureDraft): StructureCounts {
  return {
    organizations: draft.path === 3 ? Math.max(0, draft.empresas || 0) : 1,
    projects: Math.max(0, draft.proyectos || 0),
    teams: Math.max(0, draft.equipos || 0),
    salesUsers: Math.max(0, draft.asesores || 0),
    admins: Math.max(0, draft.admins || 0),
  };
}

/** A draft without a readable savedAt is of unknown age and reads as expired: every writer of this module stamps it. */
const expired = (savedAt: string | undefined): boolean => {
  const saved = savedAt === undefined ? NaN : Date.parse(savedAt);
  return Number.isNaN(saved) || Date.now() - saved > DRAFT_TTL_MS;
};

/** The saved draft; fresh when none, unreadable or past DRAFT_TTL_DAYS; inside the ceilings when the caller has them (S-16). */
export function loadStructureDraft(ceilings?: StructureCeilings): StructureDraft {
  const clamp = (draft: StructureDraft) => (ceilings ? clampStructureDraft(draft, ceilings) : draft);
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return clamp(freshDraft());
    const parsed = JSON.parse(raw) as Partial<StructureDraft>;
    if (expired(parsed.savedAt)) return clamp(freshDraft());
    return clamp({
      ...freshDraft(),
      ...parsed,
      links: parsed.links ?? {},
      assign: parsed.assign ?? {},
    });
  } catch {
    return clamp(freshDraft());
  }
}

/** Writes the draft with savedAt stamped now (S-16); the builder's autosave and the submit's applicationId write through here. */
export function saveStructureDraft(draft: StructureDraft): StructureDraft {
  const stamped: StructureDraft = { ...draft, savedAt: new Date().toISOString() };
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(stamped));
  } catch {
    /* ignore */
  }
  return stamped;
}

/** A finished company or multi-company draft (name plus a real path). */
export function isStructureDraftReady(draft: StructureDraft = loadStructureDraft()): boolean {
  if (draft.path !== 2 && draft.path !== 3) return false;
  return draft.name.trim().length > 0;
}

export function clearStructureDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
    localStorage.removeItem(DRAFT_READY_KEY);
  } catch {
    /* ignore */
  }
}

export function markStructureDraftReady(): void {
  try {
    localStorage.setItem(DRAFT_READY_KEY, '1');
  } catch {
    /* ignore */
  }
}

// [PRICING] The "Estimado" badge before a plan is chosen: seats x the monthly per-seat price of the cheapest plan the structure
// fits, to seats x Elite's, every price the book's (S-5, S-9). null = THE FAILED READ, or a structure no plan fits: the badge
// shows its one line and never a number of its own.
export function structurePriceRange(book: PriceBook, draft: StructureDraft): { low: number; high: number } | null {
  const seats = Math.max(0, (draft.asesores || 0) + (draft.admins || 0));
  const cheapest = cheapestPlanThatFits(book, structureCounts(draft));
  const low = cheapest === null ? null : planRow(book, cheapest, 1);
  const high = planRow(book, 'pro', 1);
  if (low === null || high === null) return null;
  return { low: seats * low.perSeatPerMonthMxn, high: seats * high.perSeatPerMonthMxn };
}

export function structureMoneyLabel(book: PriceBook, draft: StructureDraft): string | null {
  const range = structurePriceRange(book, draft);
  if (range === null) return null;
  const { low, high } = range;
  const fmt = (n: number) => `$${n.toLocaleString('es-MX')} MXN`;
  return `${fmt(low)} – ${fmt(high)}`;
}
